import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Dayjs } from "dayjs";
import type { FC } from "react";
import { useEffect } from "react";
import type { Control } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";
import type { AddFinalInvoiceRequestBody, Department } from "../../../api/api";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import FormCheckbox from "../../components/formComponents/FormCheckbox";
import FormDatePicker from "../../components/formComponents/FormDatePicker";
import FormNumberField from "../../components/formComponents/FormNumberField";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import { Price } from "../../misc/price";
import type { FinalInvoiceDepartmentInfo } from "./useFinalInvoiceDepartmentPayments";
import { useFinalInvoiceDepartmentPayments } from "./useFinalInvoiceDepartmentPayments";
import { usePaidAdvanceAmount } from "./usePaidAdvanceAmount";

interface Props {
  readonly open: boolean;
  readonly orderId: string;
  readonly currency: Currency;
  onClose: () => void;
  onSubmit: (request: AddFinalInvoiceRequestBody) => Promise<void>;
}

interface DepartmentPaymentFormState {
  readonly paid: boolean;
  readonly paymentDate?: Dayjs;
  readonly paidAmount: number;
}

interface FormState {
  readonly paymentDays: number;
  readonly sendByEmail: boolean;
  readonly includeOrderNameInDescription: boolean;
  readonly departmentPayments: Partial<Record<Department, DepartmentPaymentFormState>>;
}

function defaultFormState(departments: FinalInvoiceDepartmentInfo[]): FormState {
  return {
    paymentDays: 7,
    sendByEmail: true,
    includeOrderNameInDescription: false,
    departmentPayments: Object.fromEntries(
      departments.map(({ department, alreadyPaidAmount }) => [
        department,
        { paid: false, paymentDate: dayjs().subtract(1, "day"), paidAmount: alreadyPaidAmount },
      ]),
    ),
  };
}

interface DepartmentPaymentRowProps {
  readonly info: FinalInvoiceDepartmentInfo;
  readonly currency: Currency;
  readonly control: Control<FormState>;
}

const DepartmentPaymentRow: FC<DepartmentPaymentRowProps> = ({ info, currency, control }) => {
  const paidChecked = useWatch({ control, name: `departmentPayments.${info.department}.paid` });
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2">
        {info.label} — wartość usługi: {new Price(info.totalAmount).round(2).toDisplayValue(currency)}
      </Typography>
      <Grid container>
        <Grid size={12}>
          <FormCheckbox name={`departmentPayments.${info.department}.paid`} control={control} label="Opłacona" />
        </Grid>
      </Grid>
      {paidChecked ? (
        <FormDatePicker
          control={control}
          name={`departmentPayments.${info.department}.paymentDate`}
          label="Data opłacenia"
          fullWidth
        />
      ) : (
        <FormNumberField
          name={`departmentPayments.${info.department}.paidAmount`}
          control={control}
          label="Wpłacona kwota"
          suffix={currency}
          margin="dense"
          rules={{
            min: { value: 0, message: "Kwota nie może być ujemna" },
            max: { value: info.totalAmount, message: "Kwota nie może przekraczać wartości usługi" },
          }}
        />
      )}
    </Box>
  );
};

const FinalInvoiceDialog: FC<Props> = ({ open, orderId, currency, onClose, onSubmit }) => {
  const totalAdvanceAmount = usePaidAdvanceAmount(orderId);
  const departments = useFinalInvoiceDepartmentPayments(orderId);

  const { control, handleSubmit, reset } = useForm<FormState>({
    defaultValues: defaultFormState([]),
    mode: "onBlur",
  });

  useEffect(() => {
    if (open && departments.length > 0) {
      reset(defaultFormState(departments));
    }
  }, [open, departments, reset]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Faktura końcowa</DialogTitle>
      <DialogContent>
        <FormNumberField
          name="paymentDays"
          control={control}
          label="Termin płatności"
          suffix="dni"
          margin="dense"
          rules={{
            required: { value: true, message: "Termin płatności nie może być pusty" },
            min: { value: 1, message: "Liczba dni musi być większa od zera" },
          }}
        />
        {totalAdvanceAmount !== undefined && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, mb: 0.5 }}>
            <Typography variant="body2">
              Wpłacona kwota: {new Price(totalAdvanceAmount).round(2).toDisplayValue(currency)}
            </Typography>
            <Tooltip title="Suma kwot z wszystkich wystawionych faktur zaliczkowych">
              <InfoIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            </Tooltip>
          </Box>
        )}
        <Grid container>
          <Grid size={12}>
            <FormCheckbox name="sendByEmail" control={control} label="Wyślij mailem do klienta" />
          </Grid>
          <Grid size={12}>
            <FormCheckbox
              name="includeOrderNameInDescription"
              control={control}
              label="Dołącz nazwę zamówienia do opisu faktury"
            />
          </Grid>
        </Grid>
        <Divider sx={{ mt: 1 }} />
        {departments.map((info) => (
          <DepartmentPaymentRow key={info.department} info={info} currency={currency} control={control} />
        ))}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          Anuluj
        </Button>
        <AsyncLoadingButton
          onClick={handleSubmit((s) =>
            onSubmit({
              paymentDays: s.paymentDays,
              sendByEmail: s.sendByEmail,
              includeOrderNameInDescription: s.includeOrderNameInDescription,
              paymentsByDepartment: Object.fromEntries(
                Object.entries(s.departmentPayments).map(([department, payment]) => [
                  department,
                  payment.paid
                    ? { paid: true, paymentDate: payment.paymentDate?.format(ISO_DATE_FORMAT) ?? "" }
                    : payment.paidAmount > 0
                      ? { paid: payment.paidAmount }
                      : { paid: false },
                ]),
              ),
            }),
          )}
        >
          Utwórz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FinalInvoiceDialog;
